import { DjangoClient } from "api/client/DjangoClient";
import { ValidateTablesResponse } from "api/client/query_functions/wrs/Workflow/workflowValidateTables";
import { FieldParamMappingProps } from "pages/gdt/modals/fields/FieldParamMapping";
import { createStore, StoreApi } from "zustand";

type WorkflowGraph = ValidateTablesResponse;

type TableId = string;

type Result = {
  id: string;
  tableId: string;
  key: string;
  name: string;
  description: string;
};

type ResultOverrides = Record<
  Result["id"],
  Pick<Result, "description"> & { outputPolicy: "KEEP" | "DISCARD" }
>;

type TableInfo = {
  readonly id: string;
  name: string;
  readonly toolName: string;
  toolVersion: { id: number; version: string };
  readonly tableKey: string;
};

type TableProps = { name: string; key: string };

export type WorkflowEditorStoreState = {
  workflowGraph?: WorkflowGraph;
  loadingWorkflowGraph: boolean;
  tableInfo: Readonly<TableInfo>[];
  results: Result[];
  resultOverrides: ResultOverrides;
  tableInfoOverrides: Record<
    TableId,
    Omit<TableInfo, "id" | "toolName" | "tableKey">
  >;
  sourceMap: {
    sourceTableIds: string[];
    map: FieldParamMappingProps["value"];
  };
  tableProps: TableProps;
  visitedPages: Set<WorkflowEditorPageKey>;
  setTableProps: (key: keyof TableProps, value: string) => void;
  setParamMap: (
    cb: (
      map: FieldParamMappingProps["value"],
    ) => FieldParamMappingProps["value"],
  ) => void;
  setSourceTableIds: (tableIds: string[]) => void;
  updateTableList: (newTableIds: string[]) => Promise<void>;
  setTableOverride: (
    tableId: TableId,
    setOverride: (
      currentVal: Omit<TableInfo, "id" | "toolName" | "tableKey">,
    ) => Omit<TableInfo, "id" | "toolName" | "tableKey">,
  ) => void;
  setResultOverride: (
    resultId: string,
    setOverride: (
      currentVal: ResultOverrides[keyof ResultOverrides],
    ) => ResultOverrides[keyof ResultOverrides],
  ) => void;
  handleClickTimelinePage: (key: WorkflowEditorPageKey) => void;
  page: WorkflowEditorPageKey;
};

const WORKFLOW_EDITOR_PAGES = [
  { key: "TABLES", title: "Tables", pageNumber: 1 } as const,
  { key: "SOURCES", title: "Map Sources", pageNumber: 2 } as const,
  { key: "OUTPUTS", title: "Outputs", pageNumber: 3 } as const,
  { key: "NAME", title: "Name", pageNumber: 4 } as const,
];

export type WorkflowEditorPageKey =
  (typeof WORKFLOW_EDITOR_PAGES)[number]["key"];

export type WorkflowEditorPageNumber =
  (typeof WORKFLOW_EDITOR_PAGES)[number]["pageNumber"];

const PAGE_KEY_TO_PAGE_NUMBER = WORKFLOW_EDITOR_PAGES.reduce<
  Record<WorkflowEditorPageKey, WorkflowEditorPageNumber>
>(
  (map, curr) => {
    map[curr.key] = curr.pageNumber;
    return map;
  },
  {} as Record<WorkflowEditorPageKey, WorkflowEditorPageNumber>,
);

const PAGE_NUMBER_TO_PAGE_KEY = WORKFLOW_EDITOR_PAGES.reduce<
  Record<WorkflowEditorPageNumber, WorkflowEditorPageKey>
>(
  (map, curr) => {
    map[curr.pageNumber] = curr.key;
    return map;
  },
  {} as Record<WorkflowEditorPageNumber, WorkflowEditorPageKey>,
);

const LAST_PAGE_NUMBER = Math.max(
  ...WORKFLOW_EDITOR_PAGES.map((p) => p.pageNumber),
) as keyof typeof PAGE_NUMBER_TO_PAGE_KEY;
const LAST_PAGE_KEY = PAGE_NUMBER_TO_PAGE_KEY[LAST_PAGE_NUMBER];

export const WORKFLOW_EDITOR_PAGE_CONSTANTS = {
  WORKFLOW_EDITOR_PAGES,
  PAGE_KEY_TO_PAGE_NUMBER,
  PAGE_NUMBER_TO_PAGE_KEY,
  LAST_PAGE_NUMBER,
  LAST_PAGE_KEY,
};

export type WORKFLOW_EDITOR_PAGE_PARAM_VALUES =
  (typeof WORKFLOW_EDITOR_PAGES)[number]["title"];

const getWorkflowData = async (tableIds: string[]) => {
  const tableResponse = await DjangoClient.wrs.workflow.validateTables.queryFn({
    analysis_tables: tableIds.sort(),
  });

  const tableInfo = (() => {
    return tableResponse.steps.map<TableInfo & { tableKey: string }>((node) => {
      return {
        id: node.table_id,
        name: node.table_name,
        tableKey: node.table_key,
        toolVersion: node.version,
        toolName: node.tool_name,
      };
    });
  })();

  const results = (() => {
    return tableResponse.results.map<Result>((result) => {
      return {
        id: result.id,
        key: result.key,
        name: result.name,
        description: result.description,
        tableId: result.source_table_id,
      };
    });
  })();

  const tableInfoOverrides = tableInfo.reduce<
    WorkflowEditorStoreState["tableInfoOverrides"]
  >((overrides, table) => {
    overrides[table.id] = {
      name: table.name,
      toolVersion: table.toolVersion,
    };
    return overrides;
  }, {});

  const resultOverrides = results.reduce<ResultOverrides>(
    (overrides, result) => {
      overrides[result.id] = {
        description: result.description,
        outputPolicy: "KEEP",
      };
      return overrides;
    },
    {},
  );

  return {
    tableInfo,
    results,
    tableInfoOverrides,
    resultOverrides,
    workflowGraph: tableResponse,
  };
};

type CreateWorkflowEditorStoreParams = {
  tableIds?: string[];
};

export const createWorkflowEditorStore = async ({
  tableIds,
}: CreateWorkflowEditorStoreParams): Promise<
  StoreApi<WorkflowEditorStoreState>
> => {
  let tableResponse: ValidateTablesResponse | undefined;
  if (tableIds !== undefined) {
    tableResponse = await DjangoClient.wrs.workflow.validateTables.queryFn({
      analysis_tables: tableIds.sort(),
    });
  }

  const tableInfo = (() => {
    if (tableResponse !== undefined) {
      return tableResponse.steps.map<TableInfo>((node) => {
        return {
          id: node.table_id,
          name: node.name,
          table_name: node.table_name,
          tableKey: node.table_key,
          toolVersion: node.version,
          toolName: node.tool_name,
        };
      });
    }
  })();

  const results = (() => {
    if (tableResponse !== undefined) {
      return tableResponse.results.map<Result>((result) => {
        return {
          id: result.id,
          key: result.key,
          name: result.name,
          description: result.description,
          outputPolicy: undefined,
          tableId: result.source_table_id,
        };
      });
    }
  })();

  const tableInfoOverrides = tableInfo?.reduce<
    WorkflowEditorStoreState["tableInfoOverrides"]
  >((overrides, table) => {
    overrides[table.id] = {
      name: table.name,
      toolVersion: table.toolVersion,
    };
    return overrides;
  }, {});

  const resultOverrides = results?.reduce<ResultOverrides>(
    (overrides, result) => {
      overrides[result.id] = {
        description: result.description,
        outputPolicy: "KEEP",
      };
      return overrides;
    },
    {},
  );

  const store = createStore<WorkflowEditorStoreState>((set, get) => {
    return {
      workflowGraph: tableResponse,
      loadingWorkflowGraph: false,
      tableInfo: tableInfo ?? [],
      results: results ?? [],
      resultOverrides: resultOverrides ?? {},
      tableInfoOverrides: tableInfoOverrides ?? {},
      sourceMap: { map: {}, sourceTableIds: [] },
      tableProps: { key: "", name: "" },
      visitedPages: new Set(["TABLES"]),
      setTableProps: (key, val) => {
        set((prev) => ({
          ...prev,
          tableProps: { ...prev.tableProps, [key]: val },
        }));
      },
      setParamMap: (cb) => {
        set((prev) => ({
          ...prev,
          sourceMap: { ...prev.sourceMap, map: cb(prev.sourceMap.map) },
        }));
      },
      setSourceTableIds: (tableIds) => {
        set((prev) => {
          const newSourceMap = { ...prev.sourceMap, sourceTableIds: tableIds };

          return {
            ...prev,
            sourceMap: newSourceMap,
          };
        });
      },
      setTableOverride: (tableId, setOverride) => {
        set((prev) => {
          const newOverride = setOverride(get().tableInfoOverrides[tableId]);

          prev.tableInfoOverrides[tableId] = newOverride;
          return {
            ...prev,
          };
        });
      },

      setResultOverride: (resultId, setOverrides) => {
        set((prev) => {
          const newOverride = setOverrides(get().resultOverrides[resultId]);
          prev.resultOverrides[resultId] = newOverride;
          return {
            ...prev,
          };
        });
      },
      page: "TABLES",
      handleClickTimelinePage: (key) => {
        set((prev) => ({
          ...prev,
          page: key,
          visitedPages: new Set(prev.visitedPages).add(key),
        }));
      },
      updateTableList: async (tableIds) => {
        const {
          tableInfo: newTableInfo,
          results: newResults,
          tableInfoOverrides: newTableInfoOverrides,
          resultOverrides: newResultOverrides,
          workflowGraph,
        } = await getWorkflowData(tableIds);

        set((state) => {
          state.tableInfo = newTableInfo;
          state.results = newResults;

          // delete overrides for removed tables
          Object.keys(state.tableInfoOverrides).forEach((overrideId) => {
            if (newTableInfoOverrides[overrideId] === undefined) {
              delete state.tableInfoOverrides[overrideId];
            }
          });

          // add overrides of do not already exist
          Object.entries(newTableInfoOverrides).forEach(
            ([overrideId, newOverride]) => {
              if (state.tableInfoOverrides[overrideId] === undefined) {
                state.tableInfoOverrides[overrideId] = newOverride;
              }
            },
          );

          // delete overrides for removed tables
          Object.keys(state.resultOverrides).forEach((overrideId) => {
            if (newResultOverrides[overrideId] === undefined) {
              delete state.resultOverrides[overrideId];
            }
          });

          // add overrides of do not already exist
          Object.entries(newResultOverrides).forEach(
            ([overrideId, newOverride]) => {
              if (state.resultOverrides[overrideId] === undefined) {
                state.resultOverrides[overrideId] = newOverride;
              }
            },
          );

          return {
            ...state,
            workflowGraph,
            resultOverrides: { ...state.resultOverrides },
            tableInfoOverrides: { ...state.tableInfoOverrides },
          };
        });
      },
    };
  });

  return store;
};
